import { useState, useRef, useEffect, type ReactNode } from 'react';
import { ArrowRight, ChevronRight, Zap, Target, Activity, Check, Plus, Layers } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useSubscribeNewsletter } from '@workspace/api-client-react';
import { Link } from 'wouter';
import { TeamPreview } from '@/components/TeamPreview';
import { SculpturalMesh } from '@/components/SculpturalMesh';

// Assets
import heroImg from '@assets/plasmics-hero.png';
import feetneedsImg from '@assets/plasmics-printer.png';
import componentsImg from '@assets/plasmics-components.png';
import contractImg from '@assets/plasmics-contract-mfg.jpg';
import investImg from '@assets/plasmics-invest.png';
import eventsImg from '@assets/plasmics-events.png';
import newsletterImg from '@assets/plasmics-newsletter.png';

function RevealOnScroll({ children, className = '', delay = '' }: { children: React.ReactNode, className?: string, delay?: string }) {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div 
      ref={ref} 
      className={`homepage-reveal transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'} ${delay} ${className}`}
    >
      {children}
    </div>
  );
}

// A reusable tech crosshair for corners
function Crosshair({ className = '' }: { className?: string }) {
  return (
    <div className={`absolute w-3 h-3 flex items-center justify-center opacity-30 ${className}`}>
      <div className="absolute w-full h-[1px] bg-foreground"></div>
      <div className="absolute h-full w-[1px] bg-foreground"></div>
    </div>
  );
}

export default function Home({ heroBackground }: { heroBackground?: ReactNode } = {}) {
  const { toast } = useToast();
  const [email, setEmail] = useState('');
  const [consent, setConsent] = useState(false);
  const subscribe = useSubscribeNewsletter();

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!consent || subscribe.isPending) return;
    try {
      await subscribe.mutateAsync({ data: { email: email.trim(), consent: true } });
      toast({
        title: "Subscription saved",
        description: "Your email and consent have been recorded for Plasmics updates.",
      });
      setEmail('');
      setConsent(false);
    } catch {
      toast({
        title: "Subscription not saved",
        description: "We couldn't save your signup. Please try again later.",
        variant: 'destructive',
      });
    }
  };

  return (
    <main className="plasmics-home w-full bg-white overflow-hidden text-foreground">
      
      {/* HERO SECTION */}
      <section className="relative isolate overflow-hidden min-h-[100dvh] flex flex-col justify-center pt-24 pb-12 px-6 md:px-12">
        {heroBackground}
        <div aria-hidden="true" className="plasmics-mesh-layer">
          <img src={`${import.meta.env.BASE_URL}meshes/gear.svg`} alt="" className="absolute top-0 right-0 translate-x-[48%] -translate-y-[8%] w-[120vw] md:w-[70vw] lg:w-[56vw] max-w-[1000px] pointer-events-none" />
        </div>
        <div className="absolute top-0 right-0 w-full h-[50vh] bg-gradient-to-b from-white via-transparent to-transparent z-0 pointer-events-none" />
        
        <div className="container mx-auto z-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center relative">
          <Crosshair className="-top-12 -left-6" />
          <Crosshair className="-top-12 -right-6" />
          <Crosshair className="-bottom-12 -left-6" />
          <Crosshair className="-bottom-12 -right-6" />

          <div className="lg:col-span-6 flex flex-col gap-6 animate-in-fade relative z-20">
            <div className="flex items-center gap-4 mb-4">
              <span className="w-8 h-[1px] bg-primary"></span>
              <span className="font-mono text-primary text-xs font-bold tracking-[0.3em] uppercase">
                System Architecture
              </span>
            </div>
            
            <h1 className="text-5xl md:text-7xl font-sans font-light tracking-tight text-foreground leading-[1.05] text-balance">
              End-to-End <br/>
              <span className="font-bold">Manufacturing Systems.</span>
            </h1>
            
            <div className="w-12 h-1 bg-secondary my-2"></div>
            
            <p className="text-muted-foreground text-lg md:text-xl font-light max-w-lg leading-relaxed">
              A complete stack of active hardware, dense sensor networks, and real-time machine learning—deployed as autonomous production cells.
            </p>
            
            <div className="flex items-center gap-6 mt-8">
              <a href="#technology" className="px-8 py-4 bg-foreground text-background font-mono text-xs font-bold tracking-widest uppercase hover:bg-primary transition-all active:scale-95 flex items-center gap-3 group relative overflow-hidden">
                <span className="relative z-10 flex items-center gap-2">
                  Explore The Stack
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </span>
              </a>
              
              <div className="flex items-center gap-3 font-mono text-xs text-muted-foreground uppercase tracking-widest">
                <span className="relative flex items-center justify-center w-2 h-2">
                  <span className="absolute w-full h-full rounded-full bg-secondary opacity-50 animate-ping motion-reduce:animate-none"></span>
                  <span className="w-1.5 h-1.5 rounded-full bg-secondary"></span>
                </span>
                Systems Online
              </div>
            </div>
          </div>

          <div className="lg:col-span-6 animate-in-slide-up delay-200 relative">
            <div className="relative w-full aspect-[4/3] bg-white border border-border p-2 shadow-xl group">
              <div className="absolute -inset-0.5 bg-gradient-to-br from-secondary/40 to-primary/40 opacity-0 group-hover:opacity-100 transition-opacity duration-700 blur-xl -z-10" />
              <div className="w-full h-full relative overflow-hidden bg-foreground">
                <div className="absolute top-4 left-4 z-20 font-mono text-[10px] text-foreground bg-white/80 backdrop-blur px-2 py-1 border border-border">
                  PLASMICS SYSTEMS // FIELD + OUTPUT
                </div>
                <div className="w-full h-full overflow-hidden relative">
                  <img
                    src={eventsImg}
                    alt="Plasmics system demonstration"
                    className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-[2s] ease-out"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-foreground/35 via-transparent to-transparent" />
                  <span className="absolute bottom-3 left-3 font-mono text-[9px] text-white tracking-[0.18em] uppercase">
                    Systems in the field
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* OUR GOAL SECTION */}
      <section className="py-24 md:py-32 relative isolate overflow-hidden border-y border-border bg-secondary/5" id="vision">
        <div aria-hidden="true" className="plasmics-mesh-layer">
          <SculpturalMesh variant="wave" rings={24} spokes={80} twist={-0.7} tiltX={1.15} tiltZ={0.9} gradient={[0.1, 0.5, 0.5, 0.62]} className="absolute top-0 left-0 -translate-x-[28%] -translate-y-[16%] w-[155vw] md:w-[92vw] max-w-[1400px]" opacity={0.75} />
        </div>
        <div className="container mx-auto px-6 md:px-12 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <RevealOnScroll>
              <div className="relative aspect-square md:aspect-[4/3] bg-white border border-border p-4 shadow-sm group">
                <Crosshair className="-top-2 -left-2" />
                <Crosshair className="-bottom-2 -right-2" />
                <div className="w-full h-full relative bg-muted overflow-hidden">
                  <div className="absolute inset-0 border-[0.5px] border-secondary/30 m-4 z-20 pointer-events-none transition-all duration-1000 group-hover:m-2" />
                  <div className="absolute top-6 left-6 z-20 flex gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse motion-reduce:animate-none" />
                    <span className="font-mono text-[10px] text-foreground bg-white/80 backdrop-blur px-1.5 uppercase">Cell Active</span>
                  </div>
                  <img src={heroImg} alt="Plasmics production cells" className="w-full h-full object-cover grayscale-[0.5] contrast-125 mix-blend-multiply transition-transform duration-[3s] group-hover:scale-105" />
                </div>
              </div>
            </RevealOnScroll>
            
            <RevealOnScroll delay="delay-200" className="flex flex-col gap-8">
              <div>
                <span className="font-mono text-primary text-xs font-bold tracking-[0.3em] uppercase block mb-4">
                  The Plasmics Platform
                </span>
                <h2 className="text-4xl md:text-5xl font-light text-balance leading-tight">
                  A cohesive stack, <span className="font-bold">not isolated machines.</span>
                </h2>
              </div>
              
              <div className="space-y-6 text-muted-foreground text-lg font-light leading-relaxed">
                <p>
                  We engineer comprehensive manufacturing platforms. By integrating proprietary hardware, real-time perception, and adaptive software into a single deployable cell, we deliver guaranteed reliability for decentralized production.
                </p>
                <p>
                  Our customers do not just buy a printer—they deploy a <strong className="text-foreground font-bold">fully integrated production node</strong> capable of continuous, autonomous operation.
                </p>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-6 mt-4 border-t border-border pt-8">
                {[
                  "Full-Stack Integration",
                  "Active Thermal Environments",
                  "Autonomous ML Correction",
                  "Decentralized Fleet Operations"
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3 group">
                    <div className="w-5 h-5 rounded-sm border border-secondary flex items-center justify-center bg-secondary/10 mt-0.5 group-hover:bg-secondary group-hover:text-white transition-colors text-secondary">
                      <Check className="w-3 h-3" />
                    </div>
                    <span className="font-mono text-xs font-bold uppercase tracking-wide text-foreground leading-relaxed">{item}</span>
                  </div>
                ))}
              </div>
            </RevealOnScroll>
          </div>
        </div>
      </section>

      {/* APPLICATIONS SECTION */}
      <section className="py-24 md:py-32 relative isolate overflow-hidden" id="applications">
        <div aria-hidden="true" className="plasmics-mesh-layer">
          <img src={`${import.meta.env.BASE_URL}meshes/satellite.svg`} alt="" className="absolute top-52 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[90vw] md:top-[20%] md:left-[8%] md:translate-x-0 md:w-[50vw] max-w-[760px] pointer-events-none" />
        </div>
        <div className="absolute top-0 right-0 w-[50vw] h-[50vh] bg-gradient-to-bl from-white via-white/80 to-transparent z-0 pointer-events-none" />
        <div aria-hidden="true" className="plasmics-mesh-layer">
          <SculpturalMesh variant="funnel" rings={22} spokes={72} tiltX={1.1} tiltZ={2.2} gradient={[0.05, 0.05, 0.6, 0.6]} className="absolute bottom-0 right-0 translate-x-[45%] translate-y-[40%] md:translate-x-[30%] w-[100vw] md:w-[42vw] max-w-[640px]" opacity={0.8} />
        </div>
        
        <div className="container mx-auto px-6 md:px-12 relative z-10">
          <RevealOnScroll className="mb-16 md:mb-24 flex flex-col md:flex-row md:items-end justify-between gap-8 border-b border-border pb-8">
            <div className="max-w-2xl">
              <span className="font-mono text-primary text-xs font-bold tracking-[0.3em] uppercase block mb-4">
                Domain Implementations
              </span>
              <h2 className="text-4xl md:text-5xl font-light text-balance leading-tight">
                System deployments <span className="font-bold">configured for scale.</span>
              </h2>
            </div>
            <p className="text-muted-foreground max-w-sm font-light text-sm md:text-base border-l-2 border-primary pl-4">
              The Plasmics platform adapts to rigorous domain requirements, scaling seamlessly from localized medical device production to heavy industrial output.
            </p>
          </RevealOnScroll>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
            {/* Domain 1: Feetneeds (Lifestyle - Cyan focus) */}
            <RevealOnScroll delay="delay-100" className="group h-full">
              <div className="h-full flex flex-col bg-white border border-border hover:border-secondary transition-colors duration-300 relative overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-1 bg-secondary transform origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500" />
                
                <div className="p-8 pb-0">
                  <div className="w-12 h-12 bg-secondary/10 flex items-center justify-center mb-8 border border-secondary/20 group-hover:bg-secondary group-hover:text-white text-secondary transition-colors">
                    <Activity className="w-5 h-5" />
                  </div>
                  <div className="font-mono text-[10px] text-secondary tracking-widest uppercase mb-2">Lifestyle Deployment</div>
                  <h3 className="text-2xl font-bold mb-3">Feetneeds</h3>
                  <p className="text-muted-foreground font-light text-sm leading-relaxed min-h-[80px]">
                    AI-powered custom orthopedic insole manufacturing. Perfect clinical fit, produced autonomously on demand at the point of care.
                  </p>
                </div>
                
                <div className="px-8 py-4 relative h-32 overflow-hidden mt-4">
                  <div className="absolute inset-0 bg-secondary/5" />
                  <img src={feetneedsImg} alt="Feetneeds custom insole fitting" className="absolute inset-0 w-full h-full object-cover object-center mix-blend-multiply opacity-85 group-hover:scale-105 transition-transform duration-700" />
                </div>

                <div className="mt-auto p-4 border-t border-border group-hover:border-secondary/20">
                  <a href="https://feetneeds.com" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between text-foreground font-mono text-xs font-bold tracking-widest uppercase hover:text-secondary transition-colors w-full">
                    <span>View Implementation</span>
                    <Plus className="w-4 h-4 group-hover:rotate-90 transition-transform" />
                  </a>
                </div>
              </div>
            </RevealOnScroll>

            {/* Domain 2: 3D Components (Lifestyle/Tech - Mixed Gradient) */}
            <RevealOnScroll delay="delay-200" className="group h-full">
              <div className="h-full flex flex-col bg-white border border-border hover:border-foreground transition-colors duration-300 relative overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-1 plasmics-gradient transform origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500" />
                
                <div className="p-8 pb-0">
                  <div className="w-12 h-12 bg-muted flex items-center justify-center mb-8 border border-border group-hover:bg-foreground group-hover:text-white text-foreground transition-colors">
                    <Target className="w-5 h-5" />
                  </div>
                  <div className="font-mono text-[10px] text-foreground tracking-widest uppercase mb-2">Technical Deployment</div>
                  <h3 className="text-2xl font-bold mb-3">Advanced Components</h3>
                  <p className="text-muted-foreground font-light text-sm leading-relaxed min-h-[80px]">
                    Professional-grade parts engineered anywhere. Distributed manufacturing platform for high-performance lifestyle and engineering applications.
                  </p>
                </div>

                <div className="px-8 py-4 relative h-32 overflow-hidden mt-4">
                  <div className="absolute inset-0 bg-muted/50" />
                  <img src={componentsImg} alt="Components" className="absolute inset-0 w-full h-full object-cover mix-blend-multiply opacity-80 group-hover:scale-105 transition-transform duration-700" />
                </div>
                
                <div className="mt-auto p-4 border-t border-border">
                  <a href="#technology" data-testid="link-explore-capabilities" className="flex items-center justify-between text-foreground font-mono text-xs font-bold tracking-widest uppercase hover:text-primary transition-colors w-full">
                    <span>Explore Capabilities</span>
                    <Plus className="w-4 h-4 group-hover:rotate-90 transition-transform" />
                  </a>
                </div>
              </div>
            </RevealOnScroll>

            {/* Domain 3: Contract Mfg (Industrial - Orange focus) */}
            <RevealOnScroll delay="delay-300" className="group h-full">
              <div className="h-full flex flex-col bg-white border border-border hover:border-primary transition-colors duration-300 relative overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-1 bg-primary transform origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500" />
                
                <div className="p-8 pb-0">
                  <div className="w-12 h-12 bg-primary/10 flex items-center justify-center mb-8 border border-primary/20 group-hover:bg-primary group-hover:text-white text-primary transition-colors">
                    <Zap className="w-5 h-5" />
                  </div>
                  <div className="font-mono text-[10px] text-primary tracking-widest uppercase mb-2">Industrial Deployment</div>
                  <h3 className="text-2xl font-bold mb-3">Contract Manufacturing</h3>
                  <p className="text-muted-foreground font-light text-sm leading-relaxed min-h-[80px]">
                    Heavy industrial machinery, automotive, and aerospace solutions produced with extreme reliability and full material traceability.
                  </p>
                </div>

                <div className="px-8 py-4 relative h-32 overflow-hidden mt-4">
                  <div className="absolute inset-0 bg-primary/5" />
                  <img src={contractImg} alt="Contract Mfg" className="absolute inset-0 w-full h-full object-cover mix-blend-multiply opacity-80 group-hover:scale-105 transition-transform duration-700 grayscale-[0.3]" />
                </div>
                
                <div className="mt-auto p-4 border-t border-border group-hover:border-primary/20">
                  <a href={emailInquiry('Contract manufacturing partnership inquiry')} data-testid="link-partner-inquiry" className="flex items-center justify-between text-foreground font-mono text-xs font-bold tracking-widest uppercase hover:text-primary transition-colors w-full">
                    <span>Discuss Partnership</span>
                    <Plus className="w-4 h-4 group-hover:rotate-90 transition-transform" />
                  </a>
                </div>
              </div>
            </RevealOnScroll>
          </div>
        </div>
      </section>

      {/* TECHNOLOGY PILLARS */}
      <section className="py-24 md:py-32 relative bg-foreground text-background" id="technology">
        <div className="container mx-auto px-6 md:px-12 relative z-10">
          <RevealOnScroll className="max-w-3xl mb-20">
            <span className="font-mono text-secondary text-xs font-bold tracking-[0.3em] uppercase block mb-4 flex items-center gap-3">
              <Layers className="w-4 h-4" /> The Production Stack
            </span>
            <h2 className="text-4xl md:text-5xl font-light text-balance leading-tight">
              Four layers of <span className="font-bold">autonomous manufacturing.</span>
            </h2>
          </RevealOnScroll>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-24 border-y border-background/20 divide-y md:divide-y-0 md:divide-x divide-background/20">
            <RevealOnScroll delay="delay-100" className="flex flex-col gap-6 py-12 lg:py-16 lg:pr-8 group">
              <div className="font-mono text-5xl font-light text-background/20 group-hover:text-primary transition-colors">01</div>
              <div>
                <div className="font-mono text-[10px] text-primary tracking-widest uppercase mb-2">Hardware Layer</div>
                <h3 className="text-xl font-bold">Active Heated Chambers</h3>
              </div>
              <p className="text-background/70 font-light leading-relaxed text-sm">
                Built to handle highly demanding polymers. Our proprietary extrusion and active thermal control guarantee absolute environmental stability.
              </p>
            </RevealOnScroll>

            <RevealOnScroll delay="delay-200" className="flex flex-col gap-6 py-12 lg:py-16 md:pl-8 lg:px-8 group">
              <div className="font-mono text-5xl font-light text-background/20 group-hover:text-secondary transition-colors">02</div>
              <div>
                <div className="font-mono text-[10px] text-secondary tracking-widest uppercase mb-2">Perception Layer</div>
                <h3 className="text-xl font-bold">Dense Sensor Networks</h3>
              </div>
              <p className="text-background/70 font-light leading-relaxed text-sm">
                Continuous monitoring of the build environment with per-layer optical tracking, computer vision, and multidimensional telemetry.
              </p>
            </RevealOnScroll>

            <RevealOnScroll delay="delay-300" className="flex flex-col gap-6 py-12 lg:py-16 md:pr-8 lg:px-8 group">
              <div className="font-mono text-5xl font-light text-background/20 group-hover:text-white transition-colors">03</div>
              <div>
                <div className="font-mono text-[10px] text-white tracking-widest uppercase mb-2">Intelligence Layer</div>
                <h3 className="text-xl font-bold">Neural Auto-Correction</h3>
              </div>
              <p className="text-background/70 font-light leading-relaxed text-sm">
                Advanced ML anomaly detection processes sensor data in real-time, initiating autonomous corrections before a print can fail.
              </p>
            </RevealOnScroll>
            
            <RevealOnScroll delay="delay-400" className="flex flex-col gap-6 py-12 lg:py-16 md:pl-8 group">
              <div className="font-mono text-5xl font-light text-background/20 group-hover:text-primary transition-colors">04</div>
              <div>
                <div className="font-mono text-[10px] text-primary tracking-widest uppercase mb-2">Network Layer</div>
                <h3 className="text-xl font-bold">Decentralized Control</h3>
              </div>
              <p className="text-background/70 font-light leading-relaxed text-sm">
                Deploy production cells anywhere. Our platform ensures uniform quality, synchronization, and full traceability across distributed global nodes.
              </p>
            </RevealOnScroll>
          </div>
          
          <RevealOnScroll className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="flex flex-col gap-2 border-l border-primary pl-4">
              <span className="text-4xl md:text-5xl font-light text-background">10<span className="text-primary">+</span></span>
              <span className="text-xs font-mono uppercase tracking-widest text-background/60 font-bold">Sensors / Cell</span>
            </div>
            <div className="flex flex-col gap-2 border-l border-secondary pl-4">
              <span className="text-4xl md:text-5xl font-light text-background">50<span className="text-secondary">ms</span></span>
              <span className="text-xs font-mono uppercase tracking-widest text-background/60 font-bold">Response Time</span>
            </div>
            <div className="flex flex-col gap-2 border-l border-background/20 pl-4">
              <span className="text-4xl md:text-5xl font-light text-background">100<span className="text-background/50">%</span></span>
              <span className="text-xs font-mono uppercase tracking-widest text-background/60 font-bold">Quality Control</span>
            </div>
            <div className="flex flex-col gap-2 border-l border-background/20 pl-4">
              <span className="text-4xl md:text-5xl font-light text-background">24<span className="text-background/50">/</span>7</span>
              <span className="text-xs font-mono uppercase tracking-widest text-background/60 font-bold">Operation</span>
            </div>
          </RevealOnScroll>
        </div>
      </section>

      <TeamPreview />

      {/* INVESTOR CTA */}
      <section className="py-24 md:py-32 relative isolate bg-white border-b border-border overflow-hidden" id="invest">
        <div aria-hidden="true" className="plasmics-mesh-layer">
          <SculpturalMesh variant="wave" twist={1.2} tiltX={1.0} tiltZ={-0.8} gradient={[0.4, 0.05, 0.95, 0.6]} className="absolute bottom-0 left-0 -translate-x-[35%] translate-y-[35%] md:-translate-x-[15%] w-[115vw] md:w-[60vw] max-w-[950px]" opacity={0.85} />
        </div>
        <div className="absolute right-0 top-0 w-1/2 h-full opacity-10 pointer-events-none overflow-hidden">
           <img src={investImg} alt="" className="w-full h-full object-cover mix-blend-multiply" />
           <div className="absolute inset-0 bg-gradient-to-r from-white to-transparent" />
        </div>
        
        <div className="container mx-auto px-6 md:px-12 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <RevealOnScroll className="h-full bg-white/90 backdrop-blur p-8 border border-border shadow-lg">
              <span className="font-mono text-primary text-xs font-bold tracking-[0.3em] uppercase block mb-4 flex items-center gap-4">
                <span className="w-8 h-[1px] bg-primary"></span>
                System Scaling & Investment
              </span>
              <h2 className="text-4xl md:text-5xl font-light text-balance leading-[1.1] mb-6">
                Accelerate the deployment of <span className="font-bold text-foreground">integrated platforms.</span>
              </h2>
              <p className="text-muted-foreground font-light mb-10 leading-relaxed text-sm md:text-base">
                Join us in scaling the transition toward smart, decentralized industrial production. Strategic partnerships and capital allocation open now for our next system iterations.
              </p>
              <a href={emailInquiry('Investor deck request')} data-testid="link-request-investor-deck" className="px-8 py-4 bg-primary text-primary-foreground font-mono text-xs font-bold tracking-widest uppercase hover:bg-foreground hover:text-background transition-colors flex items-center justify-between w-full sm:w-auto min-w-[200px] group">
                Request Investor Deck
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform ml-4" />
              </a>
            </RevealOnScroll>

            <RevealOnScroll delay="delay-200" className="h-full bg-foreground text-background p-8 border border-foreground shadow-lg flex flex-col">
              <span className="font-mono text-secondary text-xs font-bold tracking-[0.3em] uppercase block mb-4 flex items-center gap-4">
                <span className="w-8 h-[1px] bg-secondary"></span>
                Business Partnerships
              </span>
              <h2 className="text-4xl md:text-5xl font-light text-balance leading-[1.1] mb-6">
                Build the next <span className="font-bold">production system.</span>
              </h2>
              <p className="text-background/70 font-light mb-8 leading-relaxed text-sm md:text-base">
                Collaborate with Plasmics to bring integrated manufacturing systems into new markets, materials, and production environments.
              </p>
              <div className="grid grid-cols-3 gap-3 border-y border-background/15 py-5 mb-8">
                {['Industrial', 'Technology', 'Deployment'].map((partner) => (
                  <span key={partner} className="font-mono text-[9px] leading-relaxed uppercase tracking-widest text-background/70">
                    {partner} partners
                  </span>
                ))}
              </div>
              <a href={emailInquiry('Business partnership inquiry')} data-testid="link-explore-partnerships" className="mt-auto px-8 py-4 bg-secondary text-secondary-foreground font-mono text-xs font-bold tracking-widest uppercase hover:bg-white hover:text-foreground transition-colors flex items-center justify-between w-full sm:w-auto min-w-[230px] group">
                Discuss Partnerships
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform ml-4" />
              </a>
            </RevealOnScroll>
          </div>
        </div>
      </section>

      {/* EVENTS & NEWSLETTER */}
      <section className="py-24 relative isolate overflow-hidden bg-muted/30" id="contact">
        <div aria-hidden="true" className="plasmics-mesh-layer">
          <SculpturalMesh variant="saddle" rings={24} spokes={80} twist={0.6} tiltX={1.2} tiltZ={2.6} gradient={[0.05, 0.45, 0.7, 0.95]} className="absolute top-0 right-0 translate-x-[40%] -translate-y-[40%] md:translate-x-[20%] w-[120vw] md:w-[65vw] max-w-[1050px]" opacity={0.75} />
        </div>
        <div className="container mx-auto px-6 md:px-12 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            
            {/* Events */}
            <RevealOnScroll className="relative bg-white border border-border group overflow-hidden flex flex-col">
              <div className="absolute top-0 left-0 w-1 h-full bg-secondary" />
              <div className="relative z-20 p-10 flex-1 flex flex-col justify-between">
                <div>
                  <span className="inline-block px-3 py-1 border border-secondary/30 text-secondary font-mono text-[10px] font-bold tracking-widest uppercase mb-6 bg-secondary/5">
                    Field Operations
                  </span>
                  <h3 className="text-3xl font-light leading-tight mb-2">
                    System <span className="font-bold">Demonstrations</span>
                  </h3>
                  <p className="text-sm text-muted-foreground font-light mb-8">Interested in seeing our production systems? Get in touch to discuss a briefing.</p>
                </div>
                
                <div className="border border-border p-6 mt-8 hover:border-secondary transition-colors bg-white">
                  <div className="flex items-center gap-4 mb-3 border-b border-border pb-3">
                    <div className="w-2 h-2 rounded-full bg-secondary animate-pulse motion-reduce:animate-none" />
                    <h4 className="text-lg font-bold">System Briefings</h4>
                  </div>
                  <p className="text-muted-foreground font-mono text-xs mb-6 tracking-wide">
                    Contact us to discuss availability
                  </p>
                  <a href={emailInquiry('System briefing request')} data-testid="link-request-briefing" className="text-foreground font-mono text-xs font-bold tracking-widest uppercase hover:text-secondary transition-colors flex items-center gap-2 group/btn">
                    Request a Briefing <ChevronRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                  </a>
                </div>
              </div>
            </RevealOnScroll>

            {/* Newsletter */}
            <RevealOnScroll delay="delay-200" className="relative bg-foreground text-background border border-foreground overflow-hidden flex flex-col">
              <div className="absolute top-0 left-0 w-1 h-full bg-primary" />
              <div className="absolute right-0 bottom-0 w-2/3 h-2/3 opacity-20 pointer-events-none mix-blend-screen filter grayscale">
                <img src={newsletterImg} alt="" className="w-full h-full object-contain" />
              </div>
              
              <div className="relative z-20 p-10 flex-1 flex flex-col justify-center">
                <div className="mb-10">
                  <span className="inline-block px-3 py-1 border border-primary/30 text-primary font-mono text-[10px] font-bold tracking-widest uppercase mb-6 bg-primary/5">
                    Data Stream
                  </span>
                  <h3 className="text-3xl font-light leading-tight mb-4">
                    Platform <span className="font-bold">Updates</span>
                  </h3>
                  <p className="text-background/70 font-light text-sm max-w-sm">
                     Sign up for occasional Plasmics updates about integrated manufacturing and our work.
                  </p>
                </div>
                
                <form onSubmit={handleSubscribe} className="relative w-full max-w-md">
                  <div className="flex flex-col sm:flex-row gap-4">
                    <input 
                      type="email" 
                      aria-label="Email address"
                      autoComplete="email"
                      maxLength={254}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter designation ID (Email)"
                      required
                      className="flex-1 bg-background/10 border border-background/20 px-4 py-3 text-background placeholder:text-background/40 focus:outline-none focus:border-primary transition-all font-mono text-xs"
                    />
                    <button 
                      type="submit"
                      disabled={subscribe.isPending || !consent}
                      className="px-6 py-3 bg-primary text-primary-foreground font-mono text-xs font-bold tracking-widest uppercase hover:bg-white hover:text-foreground transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {subscribe.isPending ? 'Saving…' : 'Subscribe'}
                    </button>
                  </div>
                  <label className="flex items-start gap-3 mt-5 text-background/80 text-xs leading-relaxed cursor-pointer">
                    <input
                      type="checkbox"
                      checked={consent}
                      onChange={(e) => setConsent(e.target.checked)}
                      required
                      className="mt-0.5 accent-primary"
                    />
                    <span>I agree to receive Plasmics email updates.</span>
                  </label>
                  <p className="mt-2 ml-6 text-background/70 text-xs">
                    See our <Link href="/privacy" className="underline hover:text-white">Privacy Policy</Link>.
                  </p>
                </form>
              </div>
            </RevealOnScroll>

          </div>
          <p className="mt-8 text-sm text-muted-foreground">
            For general inquiries or if your email app does not open, write to{' '}
            <a href={`mailto:${contactEmail}`} data-testid="link-general-inquiry" className="text-primary underline underline-offset-4 hover:text-foreground">{contactEmail}</a>.
          </p>
        </div>
      </section>

    </main>
  );
}

const contactEmail = 'office@plasmics.com';

const emailInquiry = (subject: string) => `mailto:${contactEmail}?subject=${encodeURIComponent(subject)}`;
